import type { PropType } from "vue";
import { watch } from "vue";
import type { ResDataFilterType } from "quick-vue-components/commonMix/loadData/types";
import componentsInit from "~/user-center/src/componentsInit";
import { loadDirectives } from "~/user-center/src/directives";
import AliOssUploadList from "~/user-center/src/components/AliOssUploadList/index.vue";
import ExportImageBtn from "~/user-center/src/components/ExportImageBtn/index.vue";
import GoodsUploadImageManage from "~/user-center/src/components/GoodsUploadImageManage/index.vue";
import AliCos from "~/user-center/src/components/SingleImageUpload/AliCos.vue";
import userCenterI18n from "~/user-center/src/utils/i18n";
import store from "~/user-center/src/store";
import { setUserCenterRouterInstance } from "~/user-center/src/router";
import { normalizeLegacyLocation } from "~/user-center/routes";

let installed = false;

function mergeUserCenterMessages(nuxtApp: any) {
  const nuxtI18n = nuxtApp.$i18n || nuxtApp.vueApp.config.globalProperties.$i18n;
  const mergeLocaleMessage = nuxtI18n?.mergeLocaleMessage || nuxtI18n?.global?.mergeLocaleMessage;
  if (!mergeLocaleMessage) {
    return;
  }

  userCenterI18n.global.availableLocales.forEach((locale) => {
    const message = userCenterI18n.global.getLocaleMessage(locale);
    if (nuxtI18n?.global?.mergeLocaleMessage) {
      nuxtI18n.global.mergeLocaleMessage(locale, message);
    }
    else {
      mergeLocaleMessage(locale, message);
    }
  });

  const localeRef = nuxtI18n?.locale || nuxtI18n?.global?.locale;
  if (!localeRef) {
    return;
  }

  watch(
    () => localeRef.value || localeRef,
    (locale) => {
      const nextLocale = String(locale || "zh-TW");
      userCenterI18n.global.locale.value = nextLocale as any;
      if (process.client) {
        window.localStorage.setItem("now-locale", nextLocale);
        window.localStorage.setItem("current_user_center_locale", nextLocale);
      }
    },
    { immediate: true },
  );
}

function patchLegacyNavigation(router: any) {
  if (router.__userCenterNavigationPatched) {
    return;
  }

  const originalPush = router.push.bind(router);
  const originalReplace = router.replace.bind(router);
  const originalResolve = router.resolve.bind(router);

  router.push = (to: any) => originalPush(normalizeLegacyLocation(to));
  router.replace = (to: any) => originalReplace(normalizeLegacyLocation(to));
  router.resolve = (to: any, currentLocation?: any) => originalResolve(normalizeLegacyLocation(to), currentLocation);
  router.__userCenterNavigationPatched = true;
}

export default defineNuxtPlugin(async (nuxtApp) => {
  if (!installed) {
    const [
      quickVueComponents,
      quickFormModule,
      dataTableModule,
    ] = await Promise.all([
      import("quick-vue-components"),
      import("quick-vue-components/QuickForm/src/index"),
      import("quick-vue-components/DataTable/src/index.vue"),
    ]);
    const {
      BaseList,
      DataList,
      DataLoad,
      DataTable,
      PowerfulBtn,
      QuickForm,
      SelectExtend,
    } = quickVueComponents;
    const { addQuickFormMemberComponents } = quickFormModule;
    const { DataTableProps } = dataTableModule as any;

    componentsInit();
    loadDirectives(nuxtApp.vueApp);
    nuxtApp.vueApp.use(store);

    addQuickFormMemberComponents({
      type: ["alisingleupload"],
      Component: AliCos,
    });
    addQuickFormMemberComponents({
      type: ["aliuploads"],
      Component: AliOssUploadList,
    });

    nuxtApp.vueApp.component("DataLoad", DataLoad);
    nuxtApp.vueApp.component("BaseList", BaseList);
    nuxtApp.vueApp.component("PowerfulBtn", PowerfulBtn);
    nuxtApp.vueApp.component("DataList", DataList);
    nuxtApp.vueApp.component("DataTable", DataTable);
    nuxtApp.vueApp.component("QuickForm", QuickForm);
    nuxtApp.vueApp.component("SelectExtend", SelectExtend);
    nuxtApp.vueApp.component("GoodsUploadImageManage", GoodsUploadImageManage);
    nuxtApp.vueApp.component("ExportImageBtn", ExportImageBtn);

    Object.assign(DataTableProps, {
      resDataFilter: {
        type: Function as PropType<ResDataFilterType>,
        default: (response: any) => ({
          list: response.data.dataList || response.data.resultList || response.data.list,
          total: response.data.totalRecord || response.data.totalResults || response.data.total,
        }),
      },
      beforeFetchDataFilter: {
        type: Function as PropType<ResDataFilterType>,
        default: (payload: any) => {
          const { currentPage, pageSize, ...otherData } = payload;
          Object.keys(otherData).forEach((key) => {
            if (typeof otherData[key] === "string" && !otherData[key]) {
              delete otherData[key];
            }
          });
          return {
            ...otherData,
            pageNo: currentPage - 1,
            pageSize,
          };
        },
      },
    });

    installed = true;
  }

  setUserCenterRouterInstance(nuxtApp.$router);
  patchLegacyNavigation(nuxtApp.$router);
  mergeUserCenterMessages(nuxtApp);
});
